import NextLink from 'next/link';
import { useLocalization } from '~/framework';
import { GetQuizSessionStruct } from '~/monolith-api';
import { docLink } from '../../../_shared/guideLink';
import { useFormatAbsoluteDateTime } from '~/ui-components';
import { useLoggedInUser, useSiteInfo } from '~/pages/_shared';
import { useQuizSessionsContext } from '../../QuizSessionsContext';
import { Stack, Tr, Td, Tbody, Link, Text, Checkbox, Tooltip } from '@chakra-ui/react';

export function TableBody() {
   const { _js } = useLocalization();
   const { data: siteInfo } = useSiteInfo();
   const { data: user } = useLoggedInUser();
   const formatter = useFormatAbsoluteDateTime();
   const { quizSessions, selection, cancelAllQuizSessions } = useQuizSessionsContext();

   const printAndTranslateStatus = (quizSession: GetQuizSessionStruct) => {
      if (quizSession.status == 'Passed') {
         return _js('Passed');
      }
      if (quizSession.status == 'Failed') {
         return _js('Failed');
      }
      if (quizSession.status == 'In-Progress') {
         return _js('In-Progress');
      }
   };

   const handleLink = (quizSession: GetQuizSessionStruct) => {
      if (quizSession.status == 'Passed' || quizSession.status == 'Failed') {
         return (
            <NextLink href={`/Quizzes/Submissions/${quizSession.quiz_sessionid}`} legacyBehavior>
               <Link color="blue.500">{_js('View result')}</Link>
            </NextLink>
         );
      }

      if (quizSession.status == 'In-Progress' && quizSession.userid == user?.userid) {
         return (
            <NextLink href={`/Quizzes/${quizSession.quizid}`} legacyBehavior>
               <Link color="blue.500">{_js('Resume')}</Link>
            </NextLink>
         );
      }
   };

   const tooltipIfPresent = (text: string | null, display?: Record<string, string>) => {
      if (!text) return '';
      return (
         <Tooltip label={text}>
            <Text width="100%" overflow="hidden" textOverflow="ellipsis" {...display}>
               {text}
            </Text>
         </Tooltip>
      );
   };

   return (
      <Tbody>
         {quizSessions &&
            quizSessions.map((quizSession, index) => (
               <Tr _hover={{ background: 'gray.100' }} key={index}>
                  <Td padding={2}>
                     <Stack direction="row" align="center">
                        {siteInfo?.['dev-feature-quizzes-v2'] &&
                           user?.permissions.canIBulkCancelQuizSessions && (
                              <Checkbox
                                 position="static"
                                 disabled={cancelAllQuizSessions}
                                 isChecked={selection.isSelected(quizSession)}
                                 onChange={() => selection.toggle(quizSession)}
                                 mr="2"
                              />
                           )}
                        <Text>{quizSession.username}</Text>
                     </Stack>
                  </Td>
                  <Td padding={2} maxWidth="250px">
                     <Stack align="center" flexDirection="row">
                        <NextLink
                           href={docLink(
                              quizSession.doc_title,
                              quizSession.docid,
                              quizSession.doctype
                           )}
                           passHref
                           legacyBehavior
                        >
                           <Link color="blue.500" width="100%" maxWidth="200px">
                              {tooltipIfPresent(quizSession.doc_title)}
                           </Link>
                        </NextLink>
                        {siteInfo?.['dev-feature-quizzes-v2'] &&
                           quizSession.doc_revision_title &&
                           tooltipIfPresent(`(${quizSession.doc_revision_title})`, {
                              color: '#7a7a7a',
                              fontSize: '14',
                              minWidth: '50px',
                           })}
                     </Stack>
                  </Td>
                  <Td maxWidth="200px" padding={2}>
                     {tooltipIfPresent(quizSession.quiz_title)}
                  </Td>
                  <Td padding={2}>
                     <Stack align="center" direction="row">
                        <p>{printAndTranslateStatus(quizSession)}</p>
                        {handleLink(quizSession)}
                     </Stack>
                  </Td>
                  <Td padding={2}>{formatter(quizSession.last_modified)}</Td>
               </Tr>
            ))}
      </Tbody>
   );
}
